import React, { useEffect, useState } from 'react';
import useStyles from './styles';
import Link from 'next/link';

type LogoProps = {
  href?: string;
  width?: number;
  height?: number;
  glitch?: boolean;
};

/** Inline SVG matches apps/site/public/img/elevator-logo-white.svg */
const LogoSvg = ({
  className,
  width,
  height,
}: {
  className?: string;
  width: number;
  height: number;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 -8 360 120"
    width={width}
    height={height}
    className={className}
    preserveAspectRatio="xMidYMid meet"
  >
    <path
      fill="#fff"
      d="M321.47,30.08h-5.64v19.85h5.64c1.48,0,2.75-.53,3.79-1.58,1.04-1.06,1.56-2.33,1.56-3.81v-9.06c0-3.6-1.78-5.39-5.34-5.39Z"
    />
    <path
      fill="#fff"
      d="M280.98,29.6c-1.45,0-2.69.51-3.72,1.52-1.03,1.01-1.55,2.25-1.55,3.69v33.13c0,1.45.52,2.68,1.55,3.69,1.03,1.01,2.27,1.52,3.72,1.52s2.68-.51,3.7-1.52c1.01-1.01,1.52-2.25,1.52-3.69v-33.13c0-1.45-.51-2.68-1.52-3.69-1.01-1.01-2.25-1.52-3.7-1.52Z"
    />
    <path
      fill="#fff"
      d="M353.39,59.44c2.19-4.62,3.32-9.71,3.32-15.11v-9.01c0-11.81-4.42-19.45-8.09-23.75C344.15,6.32,335.83.06,321.55.06h-14.75l-5.84,5.84c-5.68-3.84-12.28-5.84-19.42-5.84s-13.62,1.98-19.28,5.78L256.48.06h-29.2l-12.4,12.4h-21.44S185.26.06,185.26.06h-53.18l-4.44,6.73L120.91.06h-26.61l-13.5,13.5L67.3.06h-9.35l-5.44,5.44L47.07.06h-26.61L0,20.52v62.01l20.46,20.46h26.61l5.44-5.44,5.44,5.44h25.49l5.44-5.44,5.44,5.44h26.61l16.98-16.98,1.96,4.58h25.35s8.17,12.4,8.17,12.4h53.18l4.24-6.43,6.43,6.43h9.35l10.25-10.25c6.67,6.7,15.21,10.25,24.71,10.25,7.12,0,13.72-2.01,19.41-5.85l5.85,5.85h9.35l5.24-5.24,6.67,5.24h10.12l19.82-25.54-4.61-18.02ZM46.75,29.92h-17.26v16.91h12.61v9.4h-12.61v16.91h17.26v9.4h-26.6V20.52h26.6v9.4ZM83.11,82.54h-25.49V20.52h9.35v52.71h16.13v9.3ZM120.59,29.92h-17.26v16.91h12.61v9.4h-12.61v16.91h17.26v9.4h-26.6V20.52h26.6v9.4ZM158.9,82.17c-.1.22-.32.37-.56.37s-.46-.14-.56-.37l-26.07-60.8c-.08-.19-.06-.4.05-.57.11-.17.3-.27.51-.27h52.14c.2,0,.39.1.51.27.11.17.13.39.05.57l-26.07,60.8ZM226.23,82.26c-.11.17-.3.27-.51.27h-52.13c-.2,0-.39-.1-.51-.27-.11-.17-.13-.39-.05-.57l26.07-60.8c.1-.22.32-.37.56-.37s.46.14.56.37l26.07,60.8c.08.19.06.4-.05.57ZM256.16,29.93h-9.9v52.61h-9.35V29.93h-9.95v-9.4h29.2v9.4ZM295.71,68.05c0,4.02-1.42,7.45-4.25,10.26-2.83,2.82-6.25,4.23-10.24,4.23s-7.44-1.42-10.24-4.25c-2.8-2.83-4.2-6.25-4.2-10.24v-33.03c0-4.02,1.42-7.44,4.25-10.26,2.83-2.82,6.23-4.23,10.19-4.23s7.44,1.42,10.26,4.25c2.82,2.83,4.23,6.25,4.23,10.24v33.03ZM327.74,82.54l-5.67-23.36h-6.24v23.36h-9.35V20.52h14.75c5.21,0,9.04,1.44,11.48,4.31,2.14,2.51,3.22,6,3.22,10.49v9.01c0,4.39-1.67,8.1-5,11.13l6.93,27.07h-10.12Z"
    />
  </svg>
);

const Logo: React.FC<LogoProps> = ({ href, width = 85, height = 25, glitch }) => {
  const { classes } = useStyles();

  const [glitching, setGlitching] = useState(false);

  useEffect(() => {
    if (!glitch) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const triggerGlitch = () => {
      const duration = Math.random() * 1500 + 500;
      setGlitching(true);

      setTimeout(() => {
        setGlitching(false);
        const nextDelay = Math.random() * 60000 + 30000; // 30-60 sec
        //const nextDelay = Math.random() * 2000 + 3000; // 3-5 sec
        timeoutId = setTimeout(triggerGlitch, nextDelay);
      }, duration);
    };

    // 💥 Immediately trigger first glitch
    triggerGlitch();

    return () => clearTimeout(timeoutId);
  }, [glitch]);

  const content = (
    <div className={classes.logoWrapper}>
      {!glitch && <LogoSvg className={classes.baseSvg} width={width} height={height} />}
      {glitch && (
        <>
          <svg width="0" height="0" style={{ position: 'absolute', display: 'none' }}>
            <filter id="noiseFilter">
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.8"
                numOctaves="4"
                seed="5"
              />
            </filter>
          </svg>

          <LogoSvg className={`${classes.baseSvg}`} width={width} height={height} />
          <LogoSvg
            className={`${classes.glitchSvg} ${glitching ? classes.glitchRGB : ''}`}
            width={width}
            height={height}
          />
          <LogoSvg
            className={`${classes.glitchSvg} ${glitching ? classes.glitch1 : ''}`}
            width={width}
            height={height}
          />
          <LogoSvg
            className={`${classes.glitchSvg} ${glitching ? classes.glitch2 : ''}`}
            width={width}
            height={height}
          />
          <LogoSvg
            className={`${classes.glitchSvg} ${glitching ? classes.glitch3 : ''}`}
            width={width}
            height={height}
          />
        </>
      )}
    </div>
  );

  return href ? <Link href={href}>{content}</Link> : content;
};

export default Logo;
